import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import {
  Activity,
  Database,
  Phone,
  Volume2,
  Route,
  PhoneCall,
  RefreshCw,
  Play,
  CheckCircle,
  XCircle,
  AlertTriangle,
  AlertCircle,
  Clock,
  Server,
  Zap,
  Shield,
  ArrowRight,
  ExternalLink,
  Bell,
  BellOff,
  Eye,
  Check
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

// Status colors and icons
const STATUS_CONFIG = {
  healthy: { color: "bg-green-500", textColor: "text-green-400", icon: CheckCircle, label: "Healthy" },
  degraded: { color: "bg-yellow-500", textColor: "text-yellow-400", icon: AlertTriangle, label: "Degraded" },
  unhealthy: { color: "bg-red-500", textColor: "text-red-400", icon: XCircle, label: "Unhealthy" },
  unknown: { color: "bg-slate-500", textColor: "text-slate-400", icon: AlertCircle, label: "Unknown" }
};

const SEVERITY_CONFIG = {
  critical: { color: "bg-red-500/20 text-red-400 border-red-500/50", icon: XCircle },
  high: { color: "bg-orange-500/20 text-orange-400 border-orange-500/50", icon: AlertTriangle },
  medium: { color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/50", icon: AlertCircle },
  low: { color: "bg-blue-500/20 text-blue-400 border-blue-500/50", icon: AlertCircle },
  info: { color: "bg-slate-500/20 text-slate-400 border-slate-500/50", icon: AlertCircle }
};

const SUBSYSTEM_ICONS = {
  "Backend API": Server,
  "Database": Database,
  "Telephony (Telnyx)": Phone,
  "Voice (TTS)": Volume2,
  "Agent Routing": Route,
  "Call Processing": PhoneCall
};

export default function SystemMonitor() {
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState(null);
  const [diagnosis, setDiagnosis] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertRules, setAlertRules] = useState([]);
  const [alertStats, setAlertStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, statusRes, diagnoseRes, alertsRes, rulesRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/v1/system/summary`),
        axios.get(`${API}/api/v1/system/status`),
        axios.get(`${API}/api/v1/system/diagnose`),
        axios.get(`${API}/api/v1/alerts`),
        axios.get(`${API}/api/v1/alerts/rules`),
        axios.get(`${API}/api/v1/alerts/stats`)
      ]);
      
      setSummary(summaryRes.data);
      setStatus(statusRes.data);
      setDiagnosis(diagnoseRes.data);
      setAlerts(alertsRes.data.alerts || []);
      setAlertRules(rulesRes.data.rules || []);
      setAlertStats(statsRes.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Failed to fetch system status:", error);
    }
    setLoading(false);
  }, []);

  const runTests = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/api/v1/system/test`);
      setTestResults(res.data);
    } catch (error) {
      console.error("Failed to run tests:", error);
    }
    setTesting(false);
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      await axios.post(`${API}/api/v1/alerts/${alertId}/acknowledge`);
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Failed to acknowledge alert:", error);
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      await axios.post(`${API}/api/v1/alerts/${alertId}/resolve`);
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Failed to resolve alert:", error);
    }
  };

  const toggleRule = async (ruleId, enabled) => {
    try {
      const endpoint = enabled 
        ? `${API}/api/v1/alerts/rules/${ruleId}/enable`
        : `${API}/api/v1/alerts/rules/${ruleId}/disable`;
      await axios.post(endpoint);
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Failed to toggle rule:", error);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const getStatusIcon = (statusValue) => {
    const config = STATUS_CONFIG[statusValue] || STATUS_CONFIG.unknown;
    const Icon = config.icon;
    return <Icon className={`w-5 h-5 ${config.textColor}`} />;
  };

  const getStatusBadge = (statusValue) => {
    const config = STATUS_CONFIG[statusValue] || STATUS_CONFIG.unknown;
    return (
      <Badge className={`${config.color}/20 ${config.textColor}`}>
        {config.label}
      </Badge>
    );
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "Never";
    const date = new Date(ts);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return date.toLocaleDateString();
  };

  if (loading && !summary) {
    return (
      <div className="p-6 bg-slate-900 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading system status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-slate-900 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Activity className="w-7 h-7 text-blue-400" />
            System Monitor
          </h1>
          <p className="text-slate-400 mt-1">
            Real-time health, diagnostics, and testing
            {lastRefresh && (
              <span className="ml-2 text-slate-500">
                • Last updated: {formatTimestamp(lastRefresh.toISOString())}
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={loading}
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={runTests}
            disabled={testing}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Play className={`w-4 h-4 mr-2 ${testing ? 'animate-pulse' : ''}`} />
            {testing ? "Testing..." : "Run Tests"}
          </Button>
        </div>
      </div>

      {/* Overall Status Card */}
      {summary && (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  summary.overall_status === 'healthy' ? 'bg-green-500/20' :
                  summary.overall_status === 'degraded' ? 'bg-yellow-500/20' :
                  summary.overall_status === 'unhealthy' ? 'bg-red-500/20' : 'bg-slate-500/20'
                }`}>
                  {getStatusIcon(summary.overall_status)}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    System Status: {STATUS_CONFIG[summary.overall_status]?.label || "Unknown"}
                  </h2>
                  <p className="text-slate-400">
                    Version {summary.version} • Uptime: {Math.floor(summary.uptime_seconds / 3600)}h {Math.floor((summary.uptime_seconds % 3600) / 60)}m
                  </p>
                </div>
              </div>
              
              <div className="flex gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{summary.counts?.businesses || 0}</p>
                  <p className="text-slate-400 text-sm">Businesses</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{summary.counts?.agents || 0}</p>
                  <p className="text-slate-400 text-sm">Agents</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{summary.counts?.phone_numbers || 0}</p>
                  <p className="text-slate-400 text-sm">Numbers</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-400">{summary.issues?.critical || 0}</p>
                  <p className="text-slate-400 text-sm">Critical Issues</p>
                </div>
              </div>
            </div>

            {summary.recommended_action && (
              <Alert className="mt-4 bg-yellow-500/10 border-yellow-500/50">
                <Zap className="w-4 h-4 text-yellow-400" />
                <AlertDescription className="text-yellow-200">
                  <strong>Recommended Action:</strong> {summary.recommended_action}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800">
          <TabsTrigger value="overview" className="data-[state=active]:bg-slate-700">
            <Activity className="w-4 h-4 mr-2" /> Overview
          </TabsTrigger>
          <TabsTrigger value="alerts" className="data-[state=active]:bg-slate-700">
            <Bell className="w-4 h-4 mr-2" /> Alerts
            {alertStats?.by_status?.firing > 0 && (
              <Badge className="ml-2 bg-red-500/20 text-red-400">{alertStats.by_status.firing}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="diagnostics" className="data-[state=active]:bg-slate-700">
            <AlertTriangle className="w-4 h-4 mr-2" /> Diagnostics
            {diagnosis?.issues?.length > 0 && (
              <Badge className="ml-2 bg-red-500/20 text-red-400">{diagnosis.issues.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="tests" className="data-[state=active]:bg-slate-700">
            <Shield className="w-4 h-4 mr-2" /> Tests
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Subsystem Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {status?.subsystems?.map((subsystem) => {
              const Icon = SUBSYSTEM_ICONS[subsystem.name] || Server;
              const statusConfig = STATUS_CONFIG[subsystem.status] || STATUS_CONFIG.unknown;
              
              return (
                <Card key={subsystem.name} className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusConfig.color}/20`}>
                          <Icon className={`w-5 h-5 ${statusConfig.textColor}`} />
                        </div>
                        <div>
                          <h3 className="text-white font-medium">{subsystem.name}</h3>
                          <p className="text-slate-400 text-sm">{subsystem.message}</p>
                        </div>
                      </div>
                      {getStatusBadge(subsystem.status)}
                    </div>
                    
                    {subsystem.last_success && (
                      <div className="mt-3 pt-3 border-t border-slate-700">
                        <p className="text-slate-500 text-xs">
                          Last success: {formatTimestamp(subsystem.last_success)}
                        </p>
                      </div>
                    )}
                    
                    {subsystem.metrics && Object.keys(subsystem.metrics).length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-700 flex flex-wrap gap-3">
                        {Object.entries(subsystem.metrics).map(([key, value]) => (
                          value !== null && value !== undefined && (
                            <div key={key} className="text-xs">
                              <span className="text-slate-500">{key.replace(/_/g, ' ')}:</span>
                              <span className="text-slate-300 ml-1">{String(value)}</span>
                            </div>
                          )
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Recent Activity */}
          {status?.state && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-slate-400" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-900 p-4 rounded-lg">
                    <p className="text-slate-400 text-sm">Last Telnyx Webhook</p>
                    <p className="text-white font-medium">{formatTimestamp(status.state.last_telnyx_webhook_at)}</p>
                    {status.state.last_telnyx_webhook_event && (
                      <p className="text-slate-500 text-xs mt-1">{status.state.last_telnyx_webhook_event}</p>
                    )}
                  </div>
                  <div className="bg-slate-900 p-4 rounded-lg">
                    <p className="text-slate-400 text-sm">Last Call Started</p>
                    <p className="text-white font-medium">{formatTimestamp(status.state.last_call_started_at)}</p>
                  </div>
                  <div className="bg-slate-900 p-4 rounded-lg">
                    <p className="text-slate-400 text-sm">Last Call Connected</p>
                    <p className="text-white font-medium">{formatTimestamp(status.state.last_call_connected_at)}</p>
                  </div>
                  <div className="bg-slate-900 p-4 rounded-lg">
                    <p className="text-slate-400 text-sm">Last TTS Success</p>
                    <p className="text-white font-medium">{formatTimestamp(status.state.last_tts_success_at)}</p>
                    {status.state.last_tts_provider && (
                      <p className="text-slate-500 text-xs mt-1">{status.state.last_tts_provider}</p>
                    )}
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-900 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-400">{status.state.webhook_success_count_15m}</p>
                    <p className="text-slate-400 text-sm">Webhooks (15m)</p>
                  </div>
                  <div className="bg-slate-900 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-400">{status.state.calls_started_15m}</p>
                    <p className="text-slate-400 text-sm">Calls Started (15m)</p>
                  </div>
                  <div className="bg-slate-900 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-cyan-400">{status.state.calls_connected_15m}</p>
                    <p className="text-slate-400 text-sm">Calls Connected (15m)</p>
                  </div>
                  <div className="bg-slate-900 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-400">{status.state.recent_errors_count}</p>
                    <p className="text-slate-400 text-sm">Errors (15m)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-6">
          {/* Alert Stats Summary */}
          {alertStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-red-400">{alertStats.by_status?.firing || 0}</p>
                  <p className="text-slate-400 text-sm">Firing</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-400">{alertStats.by_status?.acknowledged || 0}</p>
                  <p className="text-slate-400 text-sm">Acknowledged</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-green-400">{alertStats.by_status?.resolved || 0}</p>
                  <p className="text-slate-400 text-sm">Resolved</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-blue-400">{alertStats.rules?.enabled || 0}</p>
                  <p className="text-slate-400 text-sm">Rules Active</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Active Alerts */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-red-400" />
                Active Alerts
              </CardTitle>
              <CardDescription>Currently firing and acknowledged alerts</CardDescription>
            </CardHeader>
            <CardContent>
              {alerts.filter(a => a.status === 'firing' || a.status === 'acknowledged').length === 0 ? (
                <div className="text-center py-8">
                  <BellOff className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                  <p className="text-slate-400">No active alerts</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts.filter(a => a.status === 'firing' || a.status === 'acknowledged').map((alert, idx) => {
                    const severityConfig = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
                    const SeverityIcon = severityConfig.icon;
                    
                    return (
                      <div 
                        key={alert.id || idx}
                        className={`p-4 rounded-lg border ${severityConfig.color}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <SeverityIcon className="w-5 h-5 mt-0.5" />
                            <div>
                              <h4 className="font-semibold text-white">{alert.title || alert.rule_name}</h4>
                              <p className="text-sm text-slate-300 mt-1">{alert.message}</p>
                              <p className="text-xs text-slate-400 mt-2">
                                Fired: {formatTimestamp(alert.fired_at)}
                                {alert.acknowledged_at && ` • Acknowledged: ${formatTimestamp(alert.acknowledged_at)}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {alert.status === 'firing' && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="border-slate-600 text-slate-300"
                                onClick={() => acknowledgeAlert(alert.id)}
                              >
                                <Eye className="w-4 h-4 mr-1" /> Ack
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="border-green-600 text-green-400"
                              onClick={() => resolveAlert(alert.id)}
                            >
                              <Check className="w-4 h-4 mr-1" /> Resolve
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Alert Rules */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-400" />
                Alert Rules
              </CardTitle>
              <CardDescription>Manage alert rule configurations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {alertRules.map((rule, idx) => {
                  const severityConfig = SEVERITY_CONFIG[rule.severity] || SEVERITY_CONFIG.info;
                  
                  return (
                    <div 
                      key={rule.id || idx}
                      className="flex items-center justify-between p-4 bg-slate-900 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <Switch 
                          checked={rule.enabled} 
                          onCheckedChange={() => toggleRule(rule.id, !rule.enabled)}
                        />
                        <div>
                          <h4 className="font-medium text-white">{rule.name}</h4>
                          <p className="text-sm text-slate-400">{rule.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={severityConfig.color}>
                          {rule.severity}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          Cooldown: {rule.cooldown_minutes}m
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Diagnostics Tab */}
        <TabsContent value="diagnostics" className="space-y-4">
          {diagnosis?.issues?.length === 0 ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-8 text-center">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white">No Issues Detected</h3>
                <p className="text-slate-400 mt-2">All systems are operating normally.</p>
              </CardContent>
            </Card>
          ) : (
            diagnosis?.issues?.map((issue, index) => {
              const severityConfig = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.info;
              const SeverityIcon = severityConfig.icon;
              
              return (
                <Card key={issue.id || index} className={`bg-slate-800 border ${severityConfig.color.split(' ')[2]}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${severityConfig.color.split(' ').slice(0, 2).join(' ')}`}>
                        <SeverityIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Badge className={severityConfig.color}>
                            {issue.severity.toUpperCase()}
                          </Badge>
                          <Badge variant="outline" className="border-slate-600 text-slate-400">
                            {issue.subsystem}
                          </Badge>
                          {issue.runbook_id && (
                            <span className="text-slate-500 text-xs">#{issue.runbook_id}</span>
                          )}
                        </div>
                        
                        <h3 className="text-white font-bold text-lg">{issue.title}</h3>
                        <p className="text-slate-400 mt-1">{issue.description}</p>
                        
                        <div className="mt-4 bg-slate-900 p-4 rounded-lg">
                          <p className="text-slate-300 text-sm">
                            <strong>Root Cause:</strong> {issue.root_cause}
                          </p>
                        </div>
                        
                        <div className="mt-4">
                          <p className="text-slate-300 font-medium mb-2">Fix Steps:</p>
                          <ol className="space-y-2">
                            {issue.fix_steps.map((step, i) => (
                              <li key={i} className="flex items-start gap-2 text-slate-400">
                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-sm">
                                  {i + 1}
                                </span>
                                <span>{step}</span>
                              </li>
                            ))}
                          </ol>
                        </div>
                        
                        {issue.evidence && Object.keys(issue.evidence).length > 0 && (
                          <div className="mt-4 pt-4 border-t border-slate-700">
                            <p className="text-slate-500 text-xs mb-2">Evidence:</p>
                            <pre className="text-xs text-slate-400 bg-slate-900 p-2 rounded overflow-x-auto">
                              {JSON.stringify(issue.evidence, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>

        {/* Tests Tab */}
        <TabsContent value="tests" className="space-y-4">
          {!testResults ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-8 text-center">
                <Shield className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white">Run Synthetic Tests</h3>
                <p className="text-slate-400 mt-2 mb-4">
                  Click "Run Tests" to verify all subsystems are working correctly.
                </p>
                <Button
                  onClick={runTests}
                  disabled={testing}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Play className="w-4 h-4 mr-2" />
                  {testing ? "Running Tests..." : "Run Tests Now"}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Test Summary */}
              <Card className={`border ${testResults.all_passed ? 'bg-green-500/10 border-green-500/50' : 'bg-red-500/10 border-red-500/50'}`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    {testResults.all_passed ? (
                      <CheckCircle className="w-12 h-12 text-green-400" />
                    ) : (
                      <XCircle className="w-12 h-12 text-red-400" />
                    )}
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        {testResults.all_passed ? "All Tests Passed" : "Some Tests Failed"}
                      </h3>
                      <p className="text-slate-400">
                        {testResults.tests.filter(t => t.passed).length} / {testResults.tests.length} tests passed
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={runTests}
                      disabled={testing}
                      className="ml-auto border-slate-600"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${testing ? 'animate-spin' : ''}`} />
                      Re-run
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Individual Tests */}
              <div className="space-y-3">
                {testResults.tests.map((test, index) => (
                  <Card key={index} className="bg-slate-800 border-slate-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {test.passed ? (
                            <CheckCircle className="w-5 h-5 text-green-400" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-400" />
                          )}
                          <div>
                            <p className="text-white font-medium">{test.test_name}</p>
                            <p className="text-slate-400 text-sm">{test.message}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={test.passed ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                            {test.passed ? "PASS" : "FAIL"}
                          </Badge>
                          <p className="text-slate-500 text-xs mt-1">
                            {test.duration_ms.toFixed(1)}ms
                          </p>
                        </div>
                      </div>
                      
                      {test.details && Object.keys(test.details).length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-700">
                          <pre className="text-xs text-slate-400 bg-slate-900 p-2 rounded overflow-x-auto">
                            {JSON.stringify(test.details, null, 2)}
                          </pre>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
